/**
 * @class
 * @classdesc
 * Universal countdown timer, for cases if countdown timer needed
 *
 *
 * @example
 * // Element timer set to christmas eve
 * <div class="countdown-timer" data-date-to-count="<%+ new Date('2020-12-24 00:00:00').getTime() %>">
 *  <div class="days-left"></div>
 *  <div class="hours-left"></div>
 *  <div class="minutes-left"></div>
 *  <div class="seconds-left"></div>
 * </div>
 */
class CountdownTimer
{
  /**
   * Countdown timer constructor
   *
   * @param {object}        timerElement     - Timer element
   * @param {null|function} callbackWhenZero - What happens when timer hits zero
   */
  constructor(timerElement, callbackWhenZero = null) {
    this._timer = null;
    this._callbackWhenZero = callbackWhenZero;

    // Get parameters from main element dataset.
    this._dateToCount = +timerElement.dataset.dateToCount,
    this._timerElements = {
      days: timerElement.querySelector('.days-left'),
      hours: timerElement.querySelector('.hours-left'),
      minutes: timerElement.querySelector('.minutes-left'),
      seconds: timerElement.querySelector('.seconds-left')
    };
    this._insertValuesToTimer();
  }

  /**
   * Calculate times left using this._parameters.dateToCount
   *
   * @returns {object} - which contains days, hours, minutes and seconds
   * @private
   */
  _calculateTimeLeft() {
    const now  = Date.now(),
      diff = this._dateToCount - now;
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: ('0' + Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))).slice(-2),
      minutes: ('0' + Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))).slice(-2),
      seconds: ('0' + Math.floor((diff % (1000 * 60)) / 1000)).slice(-2),
      currentDateOverrunsDateToCount: now >= this._dateToCount
    }
  }

  /**
   * Insert values to timer using innerHTML
   *
   * @returns {undefined}
   * @private
   */
  _insertValuesToTimer() {
    const currentCountdownValues = this._calculateTimeLeft();
    // If curernt time overruns current time, stop countdown
    if(currentCountdownValues.currentDateOverrunsDateToCount) {
      this._stopTimer();
      this._timerElements.days.innerText = '0';
      this._timerElements.hours.innerText = '00';
      this._timerElements.minutes.innerText = '00';
      this._timerElements.seconds.innerText = '00';

      // Run callback if exists
      if(typeof this._callbackWhenZero === 'function') {
        this._callbackWhenZero();
      }
    } else {
      this._timerElements.days.innerText = currentCountdownValues.days;
      this._timerElements.hours.innerText = currentCountdownValues.hours;
      this._timerElements.minutes.innerText = currentCountdownValues.minutes;
      this._timerElements.seconds.innerText = currentCountdownValues.seconds;
      this._startTimer();
    }
  }

  /**
   * Replace current timer with new setTimeout till next second
   *
   * @returns {undefined}
   * @private
   */
  _startTimer() {
    this._timer = setTimeout(() => {
      this._insertValuesToTimer();
    }, 1000);
  }

  /**
   * Stops the timer
   *
   * @returns {undefined}
   * @private
   */
  _stopTimer() {
    clearTimeout(this._timer);
  }

  /**
   * Initialize countdown timers
   *
   * @param {object|null}   element          - querySelected clock container element
   * @param {function|null} callbackWhenZero - When timer hits zero, what to do
   *
   * @static
   */
  static init(element = null, callbackWhenZero = null) {
    if(element !== null && typeof element === 'object') {
      new CountdownTimer(element, callbackWhenZero);
    } else {
      const timerElements = document.getElementsByClassName('countdown-timer');
      if(timerElements.length > 0) {
        for(let i = 0; i < timerElements.length; i++) {
          new CountdownTimer(timerElements[i]);
        }
      }
    }
  }
}

export default CountdownTimer;
